import { Account, User, UserNotificationFlag } from "01Data/Account";
import {
  AndonDataType,
  AndonFilterSettings,
  AndonSortParam,
  BuildingInfo,
  Device,
  FilterItem,
  FilterSetting,
  IFAndonData,
  LoginUser,
  Pico3,
  Sensor,
} from "01Data/DataTypes";
import { Log } from "01Data/Log";
import { StateModel } from "99Tools/useModel";
import { CheckKizaiID } from "99Tools/utils";
import API from "./API";

export class AndonData
  extends StateModel<"ready" | "updating">
  implements IFAndonData
{
  public buildings: BuildingInfo[] = [];
  public sensors: Sensor[] = [];
  public pico3: Pico3[] = [];
  public devices: Device[] = [];
  public log: Log = new Log();
  public filter: AndonFilterSettings = new AndonFilterSettings();
  public sortParamBuilding: AndonSortParam = {
    target: "buildings",
    param: "STATUS",
    order: "desc",
  };
  public sortParamSensor: AndonSortParam = {
    target: "sensors",
    param: "STATUS",
    order: "desc",
  };
  public sortParamPico3: AndonSortParam = {
    target: "pico3",
    param: "STATUS",
    order: "desc",
  };
  public errorMessage = "";

  public constructor() {
    super("ready");
  }

  public SetError(err: any, msg: string) {
    this.errorMessage = msg;
    console.log(this.errorMessage);
    console.error(err);
  }

  public async Update(account: Account) {
    if (this.getState() === "updating") {
      return;
    }
    // 表示設定があるデバイスのみ
    const id = await account.GetLoginUserId();
    await this.GetLocationAndDeviceData(id).catch((err: any) => {
      this.SetError(err, "デバイス自動更新に失敗しました。");
      return;
    });
  }

  public async GetLocationAndDeviceData(userId?: string) {
    if (this.getState() === "updating") {
      return;
    }
    this.setState("updating", false);
    const data = await API.GetBuildingPico3Sensors();
    // 建物一覧構築
    var newBuildings = data.buildings.map((item: any) => {
      return new BuildingInfo(item);
    });
    if (newBuildings.length === 0) {
      this.setState("ready", true);
      this.buildings = newBuildings;
      return;
    }
    // PICO3一覧構築
    var newPico3 = data.pico3.map((item: any) => {
      return new Pico3(item);
    });
    // センサ一覧構築
    var newSensors = data.sensors.map((item: any) => {
      return new Sensor(item);
    });
    // userIdがセットされている場合は表示設定を反映する
    if (userId) {
      const users = await User.GetUsers();
      const loginUser =
        users.find((user: User) => user.UserId === userId) ?? new User(null);
      await loginUser.GetDetails();
      // リンクしている建物のみ抽出
      newBuildings = newBuildings.filter((item: BuildingInfo) => {
        return (
          loginUser.LinkBuildings.find(
            (bid: string) => bid === item.BuildingId
          ) !== undefined
        );
      });
      // 表示設定しているセンサのみ抽出
      newSensors = newSensors.filter((item: Sensor) => {
        return (
          loginUser.SensorSettings.find((flags: UserNotificationFlag) => {
            return (
              item.BuildingId === flags.BuildingId &&
              item.SensorId === flags.SensorId &&
              flags.DisplayFlg === "true"
            );
          }) !== undefined
        );
      });
    }
    // ログページ用デバイス一覧構築
    this.devices = [];
    newSensors.forEach((item: Sensor) => {
      this.devices.push(
        new Device(
          {
            kind: CheckKizaiID(item.SensorId),
            id: item.SensorId,
            name: item.SensorName,
            StatusUpdatedAt: item.StatusUpdatedAt(),
            ComStatus: item.ComStatus,
            ImageURL: item.SensorImage,
          },
          new LoginUser(null)
        )
      );
    });
    newPico3.forEach((item: Pico3) => {
      this.devices.push(
        new Device(
          {
            kind: "pico3",
            id: item.PicoId,
            name: item.PicoName,
            StatusUpdatedAt: item.StatusUpdatedAt(),
            ComStatus: item.ComStatus,
            ImageURL: item.PicoImage,
          },
          new LoginUser(null)
        )
      );
    });
    // 建物ステータス判定
    newBuildings.forEach((item: BuildingInfo) => {
      item.SetSensorStatus(newSensors);
    });
    // 建物名取得(センサ)
    newSensors.forEach((item: Sensor) => {
      item.SetBuildingName(newBuildings);
    });
    // 建物名取得(PICO3)
    newPico3.forEach((item: Pico3) => {
      item.SetBuildingName(newBuildings);
    });

    this.buildings = newBuildings;
    this.sensors = newSensors;
    this.pico3 = newPico3;

    // ソート
    this.buildings.forEach(
      (item: BuildingInfo, index: number) => (item.originalOrder = index)
    );
    this.Sort(this.sortParamBuilding);

    this.sensors.forEach(
      (item: Sensor, index: number) => (item.originalOrder = index)
    );
    this.Sort(this.sortParamSensor);

    this.pico3.forEach(
      (item: Pico3, index: number) => (item.originalOrder = index)
    );
    this.Sort(this.sortParamPico3);

    // フィルタ一覧構築
    const filter: AndonFilterSettings = new AndonFilterSettings();
    filter.buildings.forEach((item) => {
      item.Add(new FilterItem("(すべて選択)", true));
    });
    this.buildings.forEach((item: BuildingInfo) => {
      filter.buildings[0].Add(
        new FilterItem(item.sensorStatus.textBuilding, true)
      ); // STATUS
      filter.buildings[1].Add(new FilterItem(item.BuildingName, true)); // 建物名
    });
    filter.sensors.forEach((item) => {
      item.Add(new FilterItem("(すべて選択)", true));
    });
    this.sensors.forEach((item: Sensor) => {
      filter.sensors[0].Add(
        new FilterItem(item.GetLeakageExpression().textSensor, true)
      ); // STATUS
      filter.sensors[1].Add(new FilterItem(item.SensorName, true)); // 端末名称
      filter.sensors[2].Add(new FilterItem(item.BuildingName, true)); // 建物名
      filter.sensors[3].Add(new FilterItem(item.LocationName, true)); // 設置場所
    });
    filter.pico3.forEach((item) => {
      item.Add(new FilterItem("(すべて選択)", true));
    });
    this.pico3.forEach((item: Pico3) => {
      filter.pico3[0].Add(
        new FilterItem(item.GetLeakageExpression().textPico3, true)
      ); // STATUS
      filter.pico3[1].Add(new FilterItem(item.PicoName, true)); // 端末名称
      filter.pico3[2].Add(new FilterItem(item.BuildingName, true)); // 建物名
      filter.pico3[3].Add(new FilterItem(item.LocationName, true)); // 設置場所
    });
    // フィルタ設定移行
    this.filter.buildings.forEach((param: FilterSetting, index: number) => {
      param.items.forEach((oldItem: FilterItem) => {
        const found = filter.buildings[index].items.find(
          (newItem: FilterItem) => newItem.name === oldItem.name
        );
        if (found) {
          found.bShow = oldItem.bShow;
        }
      });
    });
    this.filter.sensors.forEach((param: FilterSetting, index: number) => {
      param.items.forEach((oldItem: FilterItem) => {
        const found = filter.sensors[index].items.find(
          (newItem: FilterItem) => newItem.name === oldItem.name
        );
        if (found) {
          found.bShow = oldItem.bShow;
        }
      });
    });
    this.filter.pico3.forEach((param: FilterSetting, index: number) => {
      param.items.forEach((oldItem: FilterItem) => {
        const found = filter.pico3[index].items.find(
          (newItem: FilterItem) => newItem.name === oldItem.name
        );
        if (found) {
          found.bShow = oldItem.bShow;
        }
      });
    });
    this.filter = filter;
    // フィルタリング
    this.ApplyFilter("buildings");
    this.ApplyFilter("sensors");
    this.ApplyFilter("pico3");

    this.setState("ready", true);
  }

  public ResetFilter(target: AndonDataType, flag: boolean, colIndex: number) {
    switch (target) {
      case "buildings":
        this.filter.buildings[colIndex].items.forEach((item) => {
          item.bShow = flag;
        });
        this.ApplyFilter("buildings");
        break;
      case "sensors":
        this.filter.sensors[colIndex].items.forEach((item) => {
          item.bShow = flag;
        });
        this.ApplyFilter("sensors");
        break;
      case "pico3":
        this.filter.pico3[colIndex].items.forEach((item) => {
          item.bShow = flag;
        });
        this.ApplyFilter("pico3");
        break;

      default:
        break;
    }
  }

  public UpdateAllCheck(target: AndonDataType, colIndex: number) {
    switch (target) {
      case "buildings":
        const ib = this.filter.buildings[colIndex].items
          .slice(1)
          .findIndex((e: FilterItem) => !e.bShow);
        // check off の項目がある → すべて選択を off, なければ on
        this.filter.buildings[colIndex].items[0].bShow = ib < 0;
        break;
      case "sensors":
        const is = this.filter.sensors[colIndex].items
          .slice(1)
          .findIndex((e: FilterItem) => !e.bShow);
        // check off の項目がある → すべて選択を off, なければ on
        this.filter.sensors[colIndex].items[0].bShow = is < 0;
        break;
      case "pico3":
        const ip = this.filter.pico3[colIndex].items
          .slice(1)
          .findIndex((e: FilterItem) => !e.bShow);
        // check off の項目がある → すべて選択を off, なければ on
        this.filter.pico3[colIndex].items[0].bShow = ip < 0;
        break;

      default:
        break;
    }
  }

  public ApplyFilter(target: AndonDataType) {
    if (target === "buildings") {
      this.buildings.forEach(
        (building: BuildingInfo) => (building.bShowTop = true)
      ); // リセット
      this.buildings.forEach((building: BuildingInfo) => {
        let found = this.filter.buildings[0].items.find(
          (item: FilterItem) => item.name === building.sensorStatus.textBuilding
        );
        if (found && !found.bShow) {
          building.bShowTop = false;
          return;
        }
        found = this.filter.buildings[1].items.find(
          (item: FilterItem) => item.name === building.BuildingName
        );
        if (found && !found.bShow) {
          building.bShowTop = false;
          return;
        }
      });
      return;
    }
    if (target === "sensors") {
      this.sensors.forEach((sensor: Sensor) => (sensor.bShowTop = true)); // リセット
      this.sensors.forEach((sensor: Sensor) => {
        let found = this.filter.sensors[0].items.find(
          (item: FilterItem) =>
            item.name === sensor.GetLeakageExpression().textSensor
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
        found = this.filter.sensors[1].items.find(
          (item: FilterItem) => item.name === sensor.SensorName
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
        found = this.filter.sensors[2].items.find(
          (item: FilterItem) => item.name === sensor.BuildingName
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
        found = this.filter.sensors[3].items.find(
          (item: FilterItem) => item.name === sensor.LocationName
        );
        if (found && !found.bShow) {
          sensor.bShowTop = false;
          return;
        }
      });
      return;
    }
    if (target === "pico3") {
      this.pico3.forEach((pico3: Pico3) => (pico3.bShowTop = true)); // リセット
      this.pico3.forEach((pico3: Pico3) => {
        let found = this.filter.pico3[0].items.find(
          (item: FilterItem) =>
            item.name === pico3.GetLeakageExpression().textPico3
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
        found = this.filter.pico3[1].items.find(
          (item: FilterItem) => item.name === pico3.PicoName
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
        found = this.filter.pico3[2].items.find(
          (item: FilterItem) => item.name === pico3.BuildingName
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
        found = this.filter.pico3[3].items.find(
          (item: FilterItem) => item.name === pico3.LocationName
        );
        if (found && !found.bShow) {
          pico3.bShowTop = false;
          return;
        }
      });
      return;
    }
  }

  public Sort(param: AndonSortParam) {
    if (param.target === "buildings") {
      this.sortParamBuilding = param;
      if (param.param === "STATUS") {
        if (param.order === "asc") {
          this.buildings.sort((a: BuildingInfo, b: BuildingInfo) => {
            if (a.sensorStatus.priority === b.sensorStatus.priority) {
              return a.BuildingName.localeCompare(b.BuildingName, "ja");
            } else {
              return a.sensorStatus.priority - b.sensorStatus.priority;
            }
          });
        } else if (param.order === "desc") {
          this.buildings.sort((a: BuildingInfo, b: BuildingInfo) => {
            if (a.sensorStatus.priority === b.sensorStatus.priority) {
              return a.BuildingName.localeCompare(b.BuildingName, "ja");
            } else {
              return b.sensorStatus.priority - a.sensorStatus.priority;
            }
          });
        }
      } else if (param.param === "建物名") {
        if (param.order === "asc") {
          this.buildings.sort((a: BuildingInfo, b: BuildingInfo) =>
            a.BuildingName.localeCompare(b.BuildingName, "ja")
          );
        } else if (param.order === "desc") {
          this.buildings.sort((a: BuildingInfo, b: BuildingInfo) =>
            b.BuildingName.localeCompare(a.BuildingName, "ja")
          );
        }
      }
    } else if (param.target === "sensors") {
      this.sortParamSensor = param;
      if (param.param === "STATUS") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) => {
            if (
              a.GetLeakageExpression().priority ===
              b.GetLeakageExpression().priority
            ) {
              // 同STATUS時、更新日時降順
              return b
                .StatusUpdatedAt()
                .localeCompare(a.StatusUpdatedAt(), "ja");
            } else {
              return (
                a.GetLeakageExpression().priority -
                b.GetLeakageExpression().priority
              );
            }
          });
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) => {
            if (
              a.GetLeakageExpression().priority ===
              b.GetLeakageExpression().priority
            ) {
              // 同STATUS時、更新日時降順
              return b
                .StatusUpdatedAt()
                .localeCompare(a.StatusUpdatedAt(), "ja");
            } else {
              return (
                b.GetLeakageExpression().priority -
                a.GetLeakageExpression().priority
              );
            }
          });
        }
      } else if (param.param === "更新日時") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.StatusUpdatedAt().localeCompare(b.StatusUpdatedAt(), "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.StatusUpdatedAt().localeCompare(a.StatusUpdatedAt(), "ja")
          );
        }
      } else if (param.param === "端末名称") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.SensorName.localeCompare(b.SensorName, "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.SensorName.localeCompare(a.SensorName, "ja")
          );
        }
      } else if (param.param === "建物名") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.BuildingName.localeCompare(b.BuildingName, "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.BuildingName.localeCompare(a.BuildingName, "ja")
          );
        }
      } else if (param.param === "設置場所") {
        if (param.order === "asc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            a.LocationName.localeCompare(b.LocationName, "ja")
          );
        } else if (param.order === "desc") {
          this.sensors.sort((a: Sensor, b: Sensor) =>
            b.LocationName.localeCompare(a.LocationName, "ja")
          );
        }
      }
    } else if (param.target === "pico3") {
      this.sortParamPico3 = param;
      if (param.param === "STATUS") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) => {
            if (
              a.GetLeakageExpression().priority ===
              b.GetLeakageExpression().priority
            ) {
              // 同STATUS時、更新日時降順
              return b
                .StatusUpdatedAt()
                .localeCompare(a.StatusUpdatedAt(), "ja");
            } else {
              return (
                a.GetLeakageExpression().priority -
                b.GetLeakageExpression().priority
              );
            }
          });
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) => {
            if (
              a.GetLeakageExpression().priority ===
              b.GetLeakageExpression().priority
            ) {
              // 同STATUS時、更新日時降順
              return b
                .StatusUpdatedAt()
                .localeCompare(a.StatusUpdatedAt(), "ja");
            } else {
              return (
                b.GetLeakageExpression().priority -
                a.GetLeakageExpression().priority
              );
            }
          });
        }
      } else if (param.param === "更新日時") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.StatusUpdatedAt().localeCompare(b.StatusUpdatedAt(), "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.StatusUpdatedAt().localeCompare(a.StatusUpdatedAt(), "ja")
          );
        }
      } else if (param.param === "端末名称") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.PicoName.localeCompare(b.PicoName, "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.PicoName.localeCompare(a.PicoName, "ja")
          );
        }
      } else if (param.param === "建物名") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.BuildingName.localeCompare(b.BuildingName, "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.BuildingName.localeCompare(a.BuildingName, "ja")
          );
        }
      } else if (param.param === "設置場所") {
        if (param.order === "asc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            a.LocationName.localeCompare(b.LocationName, "ja")
          );
        } else if (param.order === "desc") {
          this.pico3.sort((a: Pico3, b: Pico3) =>
            b.LocationName.localeCompare(a.LocationName, "ja")
          );
        }
      }
    }
  }
}
